/**
 * Category scores → composite → payment adjustment.
 *
 * `composeCategoryScores` is the math of useMipsCategoryScores (moved verbatim, P1
 * 2026-10-19): modeled PI/IA/Cost use the practice's modeled percent × weight; actual
 * PI is the stored score, actual IA is totalScore/40 × weight, actual Cost is the cost
 * dashboard categoryScore/100 × weight. Composite = sum of the five parts.
 * `paymentFor` mirrors PaymentAdjustmentCard (clamp 0–100, round, Medium scenario).
 */
import { computeAdjustmentPct, determineZone } from '@/lib/payment-adjustment';

export interface MipsCategoryScores {
  qualityScore: number;
  piScore: number;
  iaScore: number;
  costScore: number;
  patientComplexityBonus: number;
}

export interface CategoryScoreInputs {
  /** practice_weights row (GET /mips/weights). */
  weights: any | null | undefined;
  /** practice_category_scores row (GET /mips/scores). */
  scores: any | null | undefined;
  /** IA calculator result (only read when IA is not modeled). */
  iaData?: { totalScore: number } | null;
  /** Cost dashboard result (only read when Cost is not modeled). */
  costData?: { summary?: { categoryScore?: number } } | null;
}

export const ZERO_CATEGORY_SCORES: MipsCategoryScores = {
  qualityScore: 0, piScore: 0, iaScore: 0, costScore: 0, patientComplexityBonus: 0,
};

export function composeCategoryScores({ weights: w, scores: s, iaData, costData }: CategoryScoreInputs): MipsCategoryScores {
  if (!(w && s)) return { ...ZERO_CATEGORY_SCORES };

  const useModeledIa = w.use_modeled_ia === true;
  const useModeledCost = w.use_modeled_cost === true;
  const weights = {
    piWeight: w.pi_weight !== undefined ? Number(w.pi_weight) : 25,
    iaWeight: w.ia_weight !== undefined ? Number(w.ia_weight) : 15,
    costWeight: w.cost_weight !== undefined ? Number(w.cost_weight) : 30,
  };

  const piScore = w.use_modeled_pi === true
    ? ((w.modeled_pi_percent ?? 0) / 100) * weights.piWeight
    : (s.pi_score !== undefined ? Number(s.pi_score) : 0);

  let iaScore = 0;
  if (useModeledIa) {
    iaScore = ((w.modeled_ia_percent ?? 0) / 100) * weights.iaWeight;
  } else if (iaData) {
    iaScore = (iaData.totalScore / 40) * weights.iaWeight;
  }

  let costScore = 0;
  if (useModeledCost) {
    costScore = ((w.modeled_cost_percent ?? 0) / 100) * weights.costWeight;
  } else if (costData?.summary?.categoryScore !== undefined) {
    costScore = (costData.summary.categoryScore / 100) * weights.costWeight;
  }

  return {
    qualityScore: s.quality_score !== undefined ? Number(s.quality_score) : 0,
    piScore,
    iaScore,
    costScore,
    patientComplexityBonus: s.patient_complexity_bonus !== undefined ? Number(s.patient_complexity_bonus) : 0,
  };
}

export function compositeOf(c: MipsCategoryScores): number {
  return c.qualityScore + c.piScore + c.iaScore + c.costScore + c.patientComplexityBonus;
}

/** Admin payment-adjustment config row (GET /api/admin/payment-adjustment-config/:year). */
export interface PaymentConfig {
  low_scenario_input: number;
  scaling_factor: number;
}

export interface PaymentResult {
  /** Composite clamped to 0–100 and rounded (the score the adjustment is read at). */
  score: number;
  /** Decimal fraction, e.g. -0.09 = -9%. 0 when no config. */
  adjustmentPct: number;
  zone: string;
}

export function paymentFor(compositeScore: number, config: PaymentConfig | null | undefined): PaymentResult {
  const score = Math.round(Math.min(100, Math.max(0, compositeScore)));
  return {
    score,
    adjustmentPct: config ? computeAdjustmentPct(score, config.low_scenario_input, config.scaling_factor) : 0,
    zone: determineZone(score),
  };
}
